const cities = [
  { name: "Richardson", zips: "75080, 75081, 75082, 75083" },
  { name: "Plano", zips: "75023, 75024, 75025, 75074, 75075" },
  { name: "Garland", zips: "75040, 75041, 75042, 75043, 75044" },
  { name: "Allen", zips: "75002, 75013" },
  { name: "University Park / Highland Park", zips: "75205, 75225" },
];

const benefits = [
  { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from your home or office in minutes." },
  { icon: "ri-truck-line", title: "Ship Your Documents", desc: "Use overnight shipping to send your documents — we handle every detail from there." },
  { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and prevent delays." },
  { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed." },
];

export default function RichardsonLocalAreaSection() {
  return (
    <section id="service-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Richardson &amp; the Dallas Metroplex
          </h2>
        </div>

        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-[#F7F8FA] rounded-lg p-8 md:p-10 border border-gray-200">
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Richardson is home to one of the most unusual geographic features in American business geography: the Telecom Corridor. Along US-75 and Campbell Road, a dense cluster of telecommunications, semiconductor, and technology companies has made Richardson one of the most internationally connected suburban communities in Texas. Employees at companies like Ericsson, Samsung, Fujitsu, and Raytheon routinely travel to parent company headquarters in Europe and Asia, often on short notice when projects shift or executive visits are scheduled. When a business trip to Stockholm or Seoul appears on the calendar with ten days to departure, a passport renewal that was meant to happen "sometime soon" suddenly becomes urgent.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              The University of Texas at Dallas sits directly within Richardson's boundaries, bringing thousands of international students, faculty, and researchers who need passports for visa purposes, international conferences, and family travel. UTD's School of Engineering and Computer Science has strong international partnerships that generate frequent travel, and the university's diverse student body includes many first-generation college students who may be applying for their first passport to study abroad or visit family overseas.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Richardson's residential neighborhoods — Canyon Creek, Spring Valley, Greenwood Hills, and the established communities near UTD — are home to a large number of dual-income professional families who travel internationally for both business and leisure. Many of these families book international vacations during school break windows, creating concentrated passport demand that collides with standard processing timelines. A family of four planning a summer Europe trip often discovers in March or April that one or more passports need renewal — and summer processing windows are the most congested of the year.
            </p>
            <p className="text-gray-600 text-base leading-[1.8]">
              Fast Passport Center handles all of it. Whether you are a Ericsson engineer heading to Stockholm, a UTD professor attending a conference in Tokyo, or a family in Canyon Creek headed to Costa Rica with six weeks of lead time, we provide the expedited, professional passport service that Richardson's diverse community demands. Start online, ship overnight, and your passport is handled by experts who understand business travel timelines and family planning windows.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-passport-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Richardson, TX</p>
                  <p className="text-white/60 text-xs mt-0.5">Serving Richardson &amp; the Telecom Corridor</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1"><i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>Remote service — covering Collin &amp; Dallas County</p>
              <p className="text-white/50 text-xs mb-5 ml-5">Ship documents overnight from anywhere in the Dallas metroplex</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  Start online, <strong className="text-white">ship your documents overnight</strong> from any FedEx or UPS location in Richardson, and we handle the rest. DFW is 20 miles away — most Richardson travelers ship documents and receive passports within 2–9 business days.
                </p>
                <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap">
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="bg-[#F7F8FA] rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 px-3 bg-white rounded-md">
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && <span className="text-gray-400 text-xs font-mono hidden sm:inline">{city.zips}</span>}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Richardson or surrounding Dallas metro communities — start online, ship overnight, and we handle the rest. No need to drive to a government passport agency.
              </p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Nearby Cities We Serve</p>
                <div className="flex flex-wrap gap-2">
                  {[{ name: "Dallas", href: "/cities/dallas" }, { name: "Plano", href: "/cities/plano" }, { name: "Frisco", href: "/cities/frisco" }, { name: "McKinney", href: "/cities/mckinney" }, { name: "Texas", href: "/states/texas" }].map((link) => (
                    <a key={link.href} href={link.href} className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-white border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap">
                      {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Richardson Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a key={i} href="https://fastpassportcenter.com" className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}